import React from 'react';

import { faPlus, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    type CreateMembershipGroupMemberFormSchemaValues,
    createMembershipGroupMemberFormSchema,
    useCreateMembershipGroupMember,
} from 'src/features/memberships/api/useCreateMembershipGroupMember';
import { accessLevelOptions } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    DialogHeader,
    DialogSeparator,
    DialogBody,
    DialogFooter,
    DialogTrigger,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from 'src/registry/ui/Dialog';
import { Form, FormField, FormItem, FormLabel, FormControlStack, FormControl, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { mapErrors } from 'src/utils/errors';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { MembershipGroupCard } from 'src/features/memberships/types';

interface MembershipGroupMemberCreateFormModalProps {
    onSuccess: (values: CreateMembershipGroupMemberFormSchemaValues) => void;
    parkingId: string;
    membershipGroupId: number;
    membershipCards?: MembershipGroupCard[];
}
const MembershipGroupMemberCreateFormModal = ({
    onSuccess,
    parkingId,
    membershipGroupId,
}: MembershipGroupMemberCreateFormModalProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const form = useForm<CreateMembershipGroupMemberFormSchemaValues>({
        resolver: zodResolver(createMembershipGroupMemberFormSchema),
        defaultValues: {
            email: '',
        },
    });

    const { mutate: createMembershipGroupMember, isPending: isPendingUpdate } = useCreateMembershipGroupMember({
        onSuccess: () => {
            if (onSuccess) {
                onSuccess(form.getValues());
            }
            onOpenChange(false);
            toast({
                title: formatMessage(membershipMessages.createMembershipGroupMemberSuccess),
                description: formatMessage(membershipMessages.createMembershipGroupMemberSuccessDescription),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            if (errorData?.email) {
                mapErrors(errorData, form.setError);
            } else {
                toast({
                    title: errorCode,
                    description: errorMessage,
                    variant: 'destructive',
                });
            }
        },
    });

    const handleSubmit = (values: CreateMembershipGroupMemberFormSchemaValues) => {
        const submitData = {
            membershipGroupId,
            parkingId,
            ...values,
        };

        createMembershipGroupMember(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        if (setIsModalOpen) setIsModalOpen(isOpen);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                    <FontAwesomeIcon className="text-primary-700" icon={faPlus} />
                    <FormattedMessage {...membershipMessages.addMember} />
                </Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faPlus} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage {...membershipMessages.addMember} />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage {...membershipMessages.addNewMember} />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <Form form={form} onSubmit={form.handleSubmit(handleSubmit)} variant="dialog">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        <FormattedMessage {...membershipMessages.email} />
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <Input type="string" placeholder="user@smartpark.bg" {...field} />
                                        </FormControl>
                                        <FormMessage>
                                            <FormattedMessage {...membershipMessages.memberEmailMessage} />
                                        </FormMessage>
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="access_level"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        <FormattedMessage {...membershipMessages.accessLevel} />
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue
                                                        placeholder={formatMessage(
                                                            membershipMessages.accessLevelPlaceholder,
                                                        )}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {accessLevelOptions.map((role) => (
                                                        <SelectItem key={role.value} value={role.value}>
                                                            {formatMessage(role.label)}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage>
                                            <FormattedMessage
                                                {...membershipMessages.accessLevelTip}
                                                values={{
                                                    b: (chunks: React.ReactNode[]) => (
                                                        <b className="font-semibold">{chunks}</b>
                                                    ),
                                                    p: (chunks: React.ReactNode[]) => (
                                                        <span>
                                                            {chunks}
                                                            <br />
                                                        </span>
                                                    ),
                                                }}
                                            />
                                        </FormMessage>
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>

                    <Button
                        disabled={isPendingUpdate}
                        type="submit"
                        onClick={form.handleSubmit(handleSubmit)}
                        className="flex-1"
                    >
                        {isPendingUpdate ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...generalMessages.saveChanges} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { MembershipGroupMemberCreateFormModal };
